<template>
  <div>
    <card-layout card-size="md" v-if="status === USER_STATUS.LOGGED_IN">
      <template v-slot:heading v-if="orgLikeTeams.length > 1">
        Select an organization
      </template>
      <template v-slot:content>
        <div v-if="orgLikeTeams.length > 1" class="card-body form-card-body pr-0">
          <teams-list
            :navigate-through-transition="true"
            :expanded-on-launch="true"
            :next-route-name="nextRouteName"
            :next-route-query="nextRouteQuery"
            class="teams-list"
          />
        </div>
        <div v-else-if="orgLikeTeams.length === 1" class="card-body">
          <p>Loading {{ orgLikeTeams[0].name }}...</p>
        </div>
        <div v-else-if="invitations && invitations.length > 0">
          <invitations :invitations="invitations" />
          <div class="text-center mt-2">
            <router-link
              :to="{
                name: ROUTE_NAMES.CREATE_ORG,
                query: $route.query
              }"
            >
              Create a new organization
            </router-link>
          </div>
        </div>
      </template>
    </card-layout>
    <toast-container />
  </div>
</template>

<script>
import Invitations from '@/components/Invitations'
import CardLayout from '@/components/CardLayout'
import ToastContainer from '@/components/ToastContainer'
import { EVENT_ACTION } from '@/constants/analytics'
import { ROUTE_NAMES } from '@/constants/routes'
import OnboardingTrackingMixin from '@/mixins/OnboardingTrackingMixin'
import TeamMixin from '@/mixins/TeamMixin'
import service from '@/service'
import { USER_STATUS } from '@/store/store'
import urlManager from '@/urlManager'
import safeJSONParse from '@/utils/safeJSONParse'
import showAPIError from '@/utils/showAPIError'
import TeamsList from '@/components/team/TeamsList'

export default {
  name: 'OrgSelect',
  components: {
    CardLayout,
    Invitations,
    ToastContainer,
    TeamsList
  },
  mixins: [TeamMixin, OnboardingTrackingMixin],
  data() {
    return {
      ROUTE_NAMES,
      USER_STATUS,
      invitations: null
    }
  },
  computed: {
    nextRouteName() {
      return this.$route.query.nextRoute || ROUTE_NAMES.YOUR_WORK
    },
    nextRouteQuery() {
      return {
        ...safeJSONParse(decodeURIComponent(this.$route.query.nextRouteQuery)),
        showDemoModal: this.$route.query.showDemoModal
      }
    },
    status() {
      return this.$store.getters.status
    },
    user() {
      return this.$store.getters.user
    },
    orgs() {
      return this.$store.getters.userOrgs
    },
    orgLikeTeams() {
      const user = this.$store.getters.user
      return user ? user.teams.filter(x => x.isOrgLike) : []
    },
    rootTeams() {
      return this.orgLikeTeams.filter(this.isRootTeam)
    },
    teamTree() {
      return this.getTeamTree('orgLikeOnly')
    }
  },
  methods: {
    /*
      Fetches the list of orgs user is invited to
     */
    async fetchUserInvites() {
      try {
        const { data: invitations } = await service.client.get(urlManager.invitations)
        this.invitations = invitations
      } catch (e) {
        showAPIError(e, undefined, 'Error while fetching invitations')
      }
    },
    trackOnboardingCompletedEvent() {
      if (this.isOnboardingViaExternalAuthInProgress()) {
        this.trackOnboardingAnalyticsEvent(EVENT_ACTION.ONBOARDING_COMPLETED)
      }
    }
  },
  async mounted() {
    const selectedOrgLikeTeamId = this.$store.getters['orgSelect/selectedOrgLikeTeamId']
    const force = this.$route.query.force
    if (this.orgs?.length === 0) {
      await this.fetchUserInvites()
      if (this.invitations.length === 0) {
        this.$router.push({
          name: ROUTE_NAMES.SET_PROFILE,
          query: this.$route.query
        })
      }
    } else {
      this.trackOnboardingCompletedEvent()
    }
    let orgId, subOrgId
    if (!force && selectedOrgLikeTeamId) {
      orgId = this.$store.getters.teamById(selectedOrgLikeTeamId).organizationId
      subOrgId = selectedOrgLikeTeamId
      const params = { orgId, subOrgId }
      if (this.nextRouteName === ROUTE_NAMES.TEAM) {
        params.teamId = subOrgId
      }
      this.$router.replace({
        name: this.nextRouteName,
        params,
        query: {
          ...this.nextRouteQuery,
          teamId: selectedOrgLikeTeamId,
          showDemoModal: this.$route.query.showDemoModal
        }
      })
    }
    if (this.orgLikeTeams.length === 1) {
      const orgId = this.orgLikeTeams[0].organizationId
      const subOrgId = this.orgLikeTeams[0].id
      const params = { orgId, subOrgId, teamId: subOrgId }
      if (this.nextRouteName === ROUTE_NAMES.TEAM) {
        params.teamId = subOrgId
      }
      this.$router.replace({
        name: this.nextRouteName,
        params,
        query: {
          ...this.nextRouteQuery,
          teamId: selectedOrgLikeTeamId,
          showDemoModal: this.$route.query.showDemoModal
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';
@import '~@/styles/forms';
/deep/ .teams-list .team-name {
  color: $brand;
  &:hover {
    text-decoration: underline;
  }
}
</style>
